import { Logo } from '@/components/atoms/Logo';
import { useAuth } from '@/store/AuthContext';
import { useSettings } from '@/store/SettingsContext';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, CircularProgress, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

const signInSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});
type SignInFormValues = z.infer<typeof signInSchema>;

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { settings } = useSettings();

  const [signInError, setSignInError] = useState('');
  const [signInLoading, setSignInLoading] = useState(false);
  const { register: registerSignIn, handleSubmit: handleSignInSubmit, formState: { errors: signInErrors } } = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
  });

  const onSignIn = async (data: SignInFormValues) => {
    setSignInError('');
    setSignInLoading(true);
    try {
      await login(data);
      navigate('/dashboard', { replace: true });
    } catch {
      setSignInError('Invalid email or password. Please try again.');
    } finally {
      setSignInLoading(false);
    }
  };

  return (
    <div className="school-login">
      <div className="login-card">

        {/* ── LEFT: Branded panel ── */}
        <div className="login-left">
          <div className={`left-logo ${settings.login_logo_left ? 'custom-left-logo' : ''}`}>
            {settings.login_logo_left ? (
              <img
                src={settings.login_logo_left}
                alt="School logo"
                style={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Logo variant="full" height={54} />
            )}
          </div>
          <img
            src="/loading.gif"
            alt="School mascot"
            className="left-mascot"
          />
          <p className="left-tagline">Empowering Young Minds Every Day</p>
        </div>

        {/* ── RIGHT: Sign-in form panel ── */}
        <div className="login-right">
          <div className="right-logo">
            {settings.login_logo_right ? (
              <img
                src={settings.login_logo_right}
                alt="School logo"
                style={{ maxHeight: 46, maxWidth: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Logo variant="full" height={42} />
            )}
          </div>
          <h1 className="form-heading">Welcome Back</h1>
          <p className="form-subtitle">Sign in to your admin account to continue.</p>

          <form onSubmit={handleSignInSubmit(onSignIn)} noValidate>
            {signInError && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2, fontSize: '13px' }}>
                {signInError}
              </Alert>
            )}

            <div className="field-group">
              <label htmlFor="login-email">Email Address</label>
              <input
                id="login-email"
                type="email"
                placeholder="admin@school.com"
                {...registerSignIn('email')}
              />
              {signInErrors.email && (
                <span className="error-text">{signInErrors.email.message}</span>
              )}
            </div>

            <div className="field-group">
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                type="password"
                placeholder="••••••••"
                {...registerSignIn('password')}
              />
              {signInErrors.password && (
                <span className="error-text">{signInErrors.password.message}</span>
              )}
            </div>

            <div className="forgot-row">
              <a href="/forgot-password">Forgot password?</a>
            </div>

            <button type="submit" className="submit-btn" disabled={signInLoading}>
              {signInLoading ? <CircularProgress size={18} color="inherit" /> : 'Sign In'}
            </button>
          </form>
        </div>

      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

        /* ── Outer wrapper ── */
        .school-login {
          font-family: 'Poppins', sans-serif;
          width: 100%;
        }

        /* ── Card ── */
        .login-card {
          display: flex;
          flex-direction: row;
          width: 100%;
          min-height: 560px;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 24px 64px rgba(0, 0, 0, 0.22);
        }

        /* ── Left branding panel ── */
        .login-left {
          width: 42%;
          background-color: ${theme.palette.primary.main};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px 32px;
          gap: 24px;
          box-sizing: border-box;
        }

        .left-logo:not(.custom-left-logo) {
          filter: brightness(0) invert(1);
          opacity: 0.95;
        }

        .left-logo.custom-left-logo {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .left-mascot {
          width: 175px;
          height: 175px;
          object-fit: contain;
          pointer-events: none;
          filter: drop-shadow(0 10px 28px rgba(0, 0, 0, 0.22));
          animation: mascotFloat 3s ease-in-out infinite;
        }

        @keyframes mascotFloat {
          0%, 100% { transform: translateY(0px);  }
          50%       { transform: translateY(-9px); }
        }

        .left-tagline {
          margin: 0;
          font-size: 13.5px;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.85);
          text-align: center;
          line-height: 1.6;
          letter-spacing: 0.3px;
        }

        /* ── Right form panel ── */
        .login-right {
          width: 58%;
          background-color: ${theme.palette.background.paper};
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 52px 56px;
          box-sizing: border-box;
        }

        .right-logo {
          margin-bottom: 28px;
        }

        .form-heading {
          margin: 0 0 6px;
          font-size: 28px;
          font-weight: 700;
          color: ${theme.palette.text.primary};
        }

        .form-subtitle {
          margin: 0 0 28px;
          font-size: 14px;
          font-weight: 400;
          color: ${theme.palette.text.secondary};
          line-height: 1.5;
        }

        /* ── Fields ── */
        .field-group {
          display: flex;
          flex-direction: column;
          margin-bottom: 16px;
        }

        .field-group label {
          font-size: 12px;
          font-weight: 600;
          color: ${theme.palette.text.secondary};
          margin-bottom: 7px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
        }

        .school-login input {
          background-color: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f3f4f6'};
          color: ${theme.palette.text.primary};
          border: 2px solid transparent;
          border-radius: 10px;
          padding: 13px 16px;
          font-size: 14px;
          font-family: inherit;
          transition: all 0.25s ease;
          box-sizing: border-box;
          width: 100%;
        }

        .school-login input:focus {
          outline: none;
          border-color: ${theme.palette.primary.main};
          background-color: ${theme.palette.background.paper};
          box-shadow: 0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)};
        }

        .error-text {
          color: #d32f2f;
          font-size: 11.5px;
          margin-top: 5px;
        }

        /* ── Forgot password ── */
        .forgot-row {
          text-align: right;
          margin: 6px 0 22px;
        }

        .forgot-row a {
          color: ${theme.palette.primary.main};
          font-size: 13px;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }

        .forgot-row a:hover {
          color: ${theme.palette.primary.dark};
          text-decoration: underline;
        }

        /* ── Submit button ── */
        .submit-btn {
          width: 100%;
          border: none;
          border-radius: 10px;
          background: linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%);
          color: #fff;
          font-family: inherit;
          font-size: 14px;
          font-weight: 600;
          padding: 14px;
          letter-spacing: 0.5px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.25s ease;
          box-shadow: 0 4px 16px ${alpha(theme.palette.primary.main, 0.35)};
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px ${alpha(theme.palette.primary.dark, 0.5)};
        }

        .submit-btn:active:not(:disabled) { transform: translateY(0); }
        .submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .submit-btn:focus { outline: none; }

        /* ── Mobile: stack vertically ── */
        @media (max-width: 768px) {
          .login-card {
            flex-direction: column;
            border-radius: 16px;
            min-height: auto;
          }

          .login-left {
            width: 100%;
            padding: 32px 24px;
            gap: 16px;
          }

          .left-mascot {
            width: 120px;
            height: 120px;
          }

          .login-right {
            width: 100%;
            padding: 36px 28px;
          }

          .right-logo { margin-bottom: 20px; }

          .form-heading { font-size: 22px; }
        }
      `}</style>
    </div>
  );
}
